import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { orderService } from '../../services/orderService';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Textarea from '../../components/common/Textarea';
import ImageWithFallback from '../../components/common/ImageWithFallback';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Skeleton from '../../components/common/Skeleton';
import Modal from '../../components/common/Modal';
import OrderTimeline from '../../components/order/OrderTimeline';
import ShipmentModal from '../../components/order/ShipmentModal';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';
import { formatOrderNumber } from '../../utils/statusBucket';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showShipModal, setShowShipModal] = useState(false);
  const [showDeliverConfirm, setShowDeliverConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getById(id);
      setOrder(data);
    } catch {
      navigate('/fornecedor/painel/pedidos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (status, extra = {}) => {
    setProcessing(true);
    try {
      await orderService.updateStatus(id, { status, ...extra });
      await load();
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await orderService.approve(id);
      setShowApproveConfirm(false);
      await load();
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setProcessing(true);
    try {
      await orderService.reject(id, rejectionReason);
      setShowRejectModal(false);
      setRejectionReason('');
      await load();
    } finally {
      setProcessing(false);
    }
  };

  const handleShip = async ({ trackingCode, carrier }) => {
    await updateStatus('shipped', { trackingCode, carrier });
    setShowShipModal(false);
  };

  const handleDeliver = async () => {
    await updateStatus('delivered');
    setShowDeliverConfirm(false);
  };

  const handleCancel = async () => {
    await updateStatus('cancelled');
    setShowCancelConfirm(false);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-4">
        <Skeleton variant="text" width="33%" />
        <Skeleton variant="block" height={240} />
      </div>
    );
  }

  if (!order) return null;

  const awaitingApproval = order.status === 'awaiting_approval';
  const canMarkPaid = ['approved', 'pending'].includes(order.status);
  const canShip = order.status === 'paid';
  const canDeliver = order.status === 'shipped';
  // Cancelamento só é permitido enquanto o pedido está no bucket PENDING
  // (Doc Seção 6.4): pending, awaiting_approval, approved. Após pagamento
  // (PAID) o caminho correto é refund, não cancel.
  const canCancel = ['pending', 'awaiting_approval', 'approved'].includes(order.status);

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/fornecedor/painel/pedidos')}
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text transition-colors mb-4"
      >
        <ArrowLeft size={16} /> Voltar para pedidos
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text mb-1">
            Pedido {formatOrderNumber(order)}
          </h1>
          <p className="text-sm text-app-text-secondary">
            Criado em {formatDateTime(order.createdAt)}
          </p>
        </div>
        <StatusBadge domain="order" status={order.statusBucket || order.status} size="md" variant="soft" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Linha do tempo</h2>
            <OrderTimeline order={order} />
          </Card>

          {/* Cliente */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Cliente</h2>
            <dl className="text-sm space-y-1.5">
              <div className="flex gap-2">
                <dt className="text-app-text-secondary w-24">Nome:</dt>
                <dd className="font-medium text-app-text">{order.customer?.name || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-app-text-secondary w-24">Email:</dt>
                <dd className="font-medium text-app-text">{order.customer?.email || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-app-text-secondary w-24">Tipo:</dt>
                <dd className="font-medium text-app-text">
                  {order.resellerUserId ? 'Via revendedor' : 'Cliente direto'}
                </dd>
              </div>
              {order.reseller && (
                <div className="flex gap-2">
                  <dt className="text-app-text-secondary w-24">Revendedor:</dt>
                  <dd className="font-medium text-app-text">{order.reseller.name}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Itens */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-app-text mb-4">Itens</h2>
            <ul className="divide-y divide-app-border">
              {(order.items || []).map((item) => (
                <li key={item.id} className="flex gap-4 py-4 first:pt-0 last:pb-0">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-app-surface-hover">
                    <ImageWithFallback
                      src={item.imageUrl}
                      alt={item.productName}
                      fallbackText={item.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-app-text truncate">{item.productName}</h3>
                    {item.variationLabel && (
                      <p className="text-sm text-app-text-secondary">{item.variationLabel}</p>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-app-text-secondary">
                      <div>
                        Qtd: <strong className="text-app-text">{item.quantity}</strong>
                      </div>
                      <div>
                        Cheio: <strong className="text-app-text">{formatPrice(item.unitFullPrice ?? item.unitResalePrice ?? item.unitPrice)}</strong>
                      </div>
                      <div>
                        A pagar: <strong className="text-app-text">{formatPrice(item.unitPrice)}</strong>
                      </div>
                      <div>
                        Comissão: <strong className="text-success">+{formatPrice(item.unitDiscount ?? item.unitCommission ?? 0)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-app-text">
                      {formatPrice(item.unitPrice * item.quantity)}
                    </p>
                    <p className="text-xs text-app-text-secondary">Total</p>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {/* Pagamentos relacionados ao pedido (Doc 8.4 — sub-aba pagamentos). */}
          {Array.isArray(order.Payments) && order.Payments.length > 0 && (
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-4">Pagamentos</h2>
              <ul className="divide-y divide-app-border">
                {order.Payments.map((p) => (
                  <li key={p.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-app-text">
                        {(p.method || 'pagamento').toUpperCase()}
                        {p.installments > 1 && ` em ${p.installments}x`}
                      </p>
                      <p className="text-xs text-app-text-secondary">
                        {p.gatewayPaymentId ? `ID: ${p.gatewayPaymentId} · ` : ''}
                        {formatDateTime(p.createdAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatPrice(p.amount)}</p>
                      <StatusBadge domain="payment" status={p.status} size="sm" />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {order.status === 'rejected' && order.rejectionReason && (
            <Card className="p-6 bg-error/5 border-error/30">
              <h2 className="text-lg font-bold text-error mb-1">Motivo da recusa</h2>
              <p className="text-sm text-error/80">{order.rejectionReason}</p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="p-6 lg:sticky lg:top-20">
            <h2 className="text-lg font-bold text-app-text mb-4">Resumo</h2>
            <dl className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <dt className="text-app-text-secondary">Subtotal:</dt>
                <dd>{formatPrice(order.subtotal)}</dd>
              </div>
              {parseFloat(order.totalDiscount ?? order.totalCommission ?? 0) > 0 && (
                <div className="flex justify-between text-success">
                  <dt>Desconto revendedor:</dt>
                  <dd className="font-semibold">-{formatPrice(order.totalDiscount ?? order.totalCommission)}</dd>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-app-border font-bold text-lg text-app-text">
                <dt>Total a receber:</dt>
                <dd className="text-primary">{formatPrice(order.totalToPay)}</dd>
              </div>
            </dl>

            <div className="space-y-2">
              {awaitingApproval && (
                <>
                  <Button variant="accent" fullWidth onClick={() => setShowApproveConfirm(true)}>
                    Aprovar pedido
                  </Button>
                  <Button variant="danger" fullWidth onClick={() => setShowRejectModal(true)}>
                    Recusar pedido
                  </Button>
                </>
              )}
              {canMarkPaid && (
                <Button fullWidth onClick={() => updateStatus('paid')} loading={processing}>
                  Marcar como pago
                </Button>
              )}
              {canShip && (
                <Button fullWidth onClick={() => setShowShipModal(true)}>
                  Marcar como enviado
                </Button>
              )}
              {canDeliver && (
                <>
                  {order.trackingCode && (
                    <p className="text-xs text-app-text-secondary">
                      Rastreio: <span className="font-mono font-semibold">{order.trackingCode}</span>
                    </p>
                  )}
                  <Button fullWidth onClick={() => setShowDeliverConfirm(true)}>
                    Marcar como entregue
                  </Button>
                </>
              )}
              {canCancel && (
                <Button variant="danger" fullWidth onClick={() => setShowCancelConfirm(true)}>
                  Cancelar pedido
                </Button>
              )}
              {order.status === 'delivered' && order.trackingCode && (
                <p className="text-xs text-app-text-secondary">
                  Rastreio: <span className="font-mono font-semibold">{order.trackingCode}</span>
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Modals */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        onConfirm={handleApprove}
        title="Aprovar pedido?"
        message="O cliente será notificado e o pagamento poderá ser gerado."
        confirmLabel="Aprovar"
        variant="accent"
        loading={processing}
      />
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancel}
        title="Cancelar pedido?"
        message="Essa ação não pode ser desfeita."
        confirmLabel="Cancelar pedido"
        variant="danger"
        loading={processing}
      />
      <ConfirmDialog
        isOpen={showDeliverConfirm}
        onClose={() => setShowDeliverConfirm(false)}
        onConfirm={handleDeliver}
        title="Marcar como entregue?"
        message="Confirmação de que o pedido chegou ao destino."
        confirmLabel="Marcar entregue"
        variant="accent"
        loading={processing}
      />
      <ShipmentModal
        isOpen={showShipModal}
        onClose={() => setShowShipModal(false)}
        onConfirm={handleShip}
        defaultTrackingCode={order.trackingCode || ''}
        loading={processing}
      />
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason('');
        }}
        title="Recusar pedido"
        size="sm"
        footer={
          <>
            <Button variant="subtle" onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleReject} loading={processing} disabled={!rejectionReason.trim()}>
              Recusar
            </Button>
          </>
        }
      >
        <Textarea
          label="Motivo da recusa"
          required
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Ex: produto temporariamente sem estoque"
          rows={4}
        />
        <p className="mt-2 text-xs text-app-text-secondary">
          Essa mensagem será enviada ao cliente.
        </p>
      </Modal>
    </div>
  );
};

export default OrderDetail;
